// Error reporting: always console.error, and a count for the ops daily summary (the day's error
// count plus the top 3 messages, src/utils/notify). App errors never get their own message.
// Server code records directly; browser code relays through /api/client-error.
// errors.add never throws, never awaits and never blocks its caller.

/** Optional second argument: a source label ("settings/billing"), or details. */
export type ErrorContext = string | { source?: string; userId?: string; route?: string }

export type ErrorReport = {
    message: string
    name?: string
    code?: string
    stack?: string
    source?: string
    route?: string
    userId?: string
    runtime: "server" | "browser"
}

const DEDUPE_WINDOW_MS = 10 * 60 * 1000 // browser relay: identical errors once per 10 minutes per tab/process
const MAX_SENDS_PER_HOUR = 20 // browser relay: hard cap per tab / per relay process
const MAX_DEDUPE_KEYS = 500
const MAX_RECORDS_PER_MINUTE = 120 // server: counter writes per process per minute (error storms)

const lastSent = new Map<string, number>()
let hourStart = 0
let sentThisHour = 0

const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi

/** Strips email addresses and caps length. */
export function scrub(value: unknown, max: number): string | undefined {
    if (value === undefined || value === null || value === "") return undefined
    return String(value).replace(EMAIL_RE, "[email]").slice(0, max)
}

/** Turns whatever was thrown (Error, Supabase error object, string, anything) into a report. */
export function normalizeError(error: unknown, context?: ErrorContext, runtime: ErrorReport["runtime"] = typeof window === "undefined" ? "server" : "browser"): ErrorReport {
    const ctx = typeof context === "string" ? { source: context } : (context ?? {})
    const e = (typeof error === "object" && error !== null ? error : {}) as Record<string, unknown>
    let message: string
    if (typeof error === "string") message = error
    else if (typeof e.message === "string" && e.message) message = e.message
    else {
        try {
            message = JSON.stringify(error) ?? String(error)
        } catch {
            message = String(error)
        }
    }
    const route = ctx.route ?? (runtime === "browser" && typeof window !== "undefined" ? window.location.pathname : undefined)
    return {
        message: scrub(message, 1000) || "Unknown error",
        name: scrub(typeof e.name === "string" ? e.name : undefined, 80),
        code: scrub(typeof e.code === "string" || typeof e.code === "number" ? e.code : undefined, 80),
        stack: scrub(typeof e.stack === "string" ? e.stack : undefined, 3000),
        source: scrub(ctx.source, 80),
        route: scrub(route, 200),
        userId: scrub(ctx.userId, 80),
        runtime
    }
}

/** True when this report may be sent now: not a repeat inside the dedupe window and under the hourly cap. */
export function shouldSend(report: ErrorReport, now = Date.now()): boolean {
    const key = `${report.runtime}|${report.source ?? ""}|${report.message}`
    const prev = lastSent.get(key)
    if (prev !== undefined && now - prev < DEDUPE_WINDOW_MS) return false
    if (now - hourStart >= 60 * 60 * 1000) {
        hourStart = now
        sentThisHour = 0
    }
    if (sentThisHour >= MAX_SENDS_PER_HOUR) return false
    sentThisHour++
    if (lastSent.size >= MAX_DEDUPE_KEYS) {
        for (const [k, t] of lastSent) if (now - t >= DEDUPE_WINDOW_MS) lastSent.delete(k)
        if (lastSent.size >= MAX_DEDUPE_KEYS) lastSent.clear()
    }
    lastSent.set(key, now)
    return true
}

let minuteStart = 0
let recordsThisMinute = 0

/** Server only: counts one report for the daily summary (count + top messages). Resolves false
 *  (never rejects) when not recorded. */
export async function recordErrorReport(report: ErrorReport, now = Date.now()): Promise<boolean> {
    try {
        if (typeof window !== "undefined") return false
        if (now - minuteStart >= 60_000) {
            minuteStart = now
            recordsThisMinute = 0
        }
        if (++recordsThisMinute > MAX_RECORDS_PER_MINUTE) return false
        const { recordAppError } = await import("@/utils/notify/notify")
        return await recordAppError(report.message, report.runtime === "browser" ? `browser${report.source ? ` ${report.source}` : ""}` : report.source)
    } catch (recordError) {
        console.error("[errors] error counter failed:", recordError)
        return false
    }
}

/** Keeps a serverless function alive until the send finishes, when Vercel's request context exists. */
export function keepAlive(promise: Promise<unknown>) {
    try {
        const ctx = (globalThis as any)[Symbol.for("@vercel/request-context")]?.get?.()
        ctx?.waitUntil?.(promise)
    } catch {}
}

function report(error: unknown, context?: ErrorContext) {
    const r = normalizeError(error, context)
    if (r.runtime === "server") {
        // Every server error is counted (before any dedupe) for the daily summary.
        keepAlive(recordErrorReport(r))
        return
    }
    if (!shouldSend(r)) return
    if (process.env.NODE_ENV !== "production") return
    const body = JSON.stringify(r)
    fetch("/api/client-error", { method: "POST", headers: { "content-type": "application/json" }, body, keepalive: true }).catch(() => {})
}

export const errors = {
    add: (error: unknown, context?: ErrorContext) => {
        try {
            console.error(error)
        } catch {}
        try {
            report(error, context)
        } catch {}
    }
}
